import { Css, Step, useComputed } from "@homebound/beam";
import { ObjectConfig, ObjectState, required, useFormState } from "@homebound/form-state";
import { useMemo } from "react";
import { stepperBarHeight, StepperProvider, useStepperContext } from "src/components/stepper";
import { useHiddenProjectSideNav } from "src/hooks";
import { useRouteParams } from "src/hooks/useRouteParams";
import { DefineChangeStep } from "src/routes/projects/change-events/guided/components/1.DefineChangeStep";
import { SelectConfigurationStep } from "src/routes/projects/change-events/guided/components/2.SelectConfigurationStep";
import { GuidedChangeEventStepActions } from "src/routes/projects/change-events/guided/components/GuidedChangeEventStepActions";
import {
  GuidedChangeEventConfigType,
  GuidedChangeEventStep,
  GuidedChangeType,
} from "src/routes/projects/change-events/guided/utils";
import { ProjectHeader } from "src/routes/projects/ProjectHeader";
import { ProjectParams } from "src/routes/routesDef";
import { createChangeEventsUrl } from "src/RouteUrls";

export function CreateGuidedChangeEventPage() {
  // Hide the project side nav for a full-screen wizard since this component renders inside `ProjectLayout`.
  useHiddenProjectSideNav();

  const formState = useFormState({ config: formConfig });

  const steps = useComputed<Step[]>(() => {
    const defineChangeComplete = !!formState.changeType.value && !!formState.reasonId.value;
    const selectConfigComplete = !!formState.selectedConfigId.value;
    return [
      {
        label: "Describe Change",
        value: GuidedChangeEventStep.DefineChange,
        state: defineChangeComplete ? "complete" : "incomplete",
      },
      {
        label: "Select New Config",
        value: GuidedChangeEventStep.SelectConfiguration,
        state: selectConfigComplete ? "complete" : "incomplete",
        disabled: !defineChangeComplete,
      },
      {
        label: "Review New Config",
        value: GuidedChangeEventStep.ReviewConfiguration,
        state: "incomplete",
        disabled: !selectConfigComplete,
      },
      { label: "Review Impact", value: GuidedChangeEventStep.ReviewImpact, state: "incomplete", disabled: true },
      { label: "Review", value: GuidedChangeEventStep.Review, state: "incomplete", disabled: true },
    ];
  }, [formState]);

  return (
    <StepperProvider steps={steps}>
      <GuidedChangeEventWizard formState={formState} />
    </StepperProvider>
  );
}

// Export for testing
export type GuidedChangeEventFormInput = {
  changeType?: GuidedChangeType | null;
  reasonId?: string | null;
  /** Apply Full or Partial Configuration fields. */
  selectedConfigType?: GuidedChangeEventConfigType | null;
  selectedConfigId?: string | null;
  /** TODO: Add other flow-specific fields. */
};
export type GuidedChangeEventFormState = ObjectState<GuidedChangeEventFormInput>;

const formConfig: ObjectConfig<GuidedChangeEventFormInput> = {
  changeType: { type: "value", rules: [required] },
  reasonId: { type: "value", rules: [required] },
  selectedConfigId: { type: "value" },
  selectedConfigType: { type: "value" },
};

type GuidedChangeEventWizardProps = { formState: GuidedChangeEventFormState };

function GuidedChangeEventWizard(props: GuidedChangeEventWizardProps) {
  const { formState } = props;
  const { projectId } = useRouteParams<ProjectParams>();
  const { currentStep } = useStepperContext();

  const step = useMemo(() => {
    switch (currentStep.value) {
      case GuidedChangeEventStep.DefineChange:
        return <DefineChangeStep formState={formState} />;
      case GuidedChangeEventStep.SelectConfiguration:
        return <SelectConfigurationStep formState={formState} />;
      default:
        return <div css={Css.sm.gray700.$}>This step is not part of the prototype yet.</div>;
    }
  }, [currentStep.value, formState]);

  return (
    <div css={Css.mbPx(stepperBarHeight).$}>
      <ProjectHeader projectId={projectId} title="Create Change Event" createUrl={createChangeEventsUrl} />
      <div css={Css.df.jcc.$}>
        <div css={Css.wPx(760).$}>{step}</div>
      </div>
      <GuidedChangeEventStepActions formState={formState} projectId={projectId} />
    </div>
  );
}
